import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Modal } from "../../app/Modal";
import { getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";
import { isPdf } from "../../lib/pdf"; // stelt ook de pdf.js-worker in
import { MIN_ZONE, normRect, type Zone } from "../../lib/zones";
import { IconClose } from "../../app/icons";

const COLORS = ["#ec663a", "#2f80ed", "#27ae60", "#9b51e0", "#f2c94c", "#eb5757"];

/**
 * Zones op één tekening: per kader een verdieping. Kaders trekken met muis, vinger of pen;
 * naam, afkorting en verwijderen in de lijst eronder (ook met het toetsenbord).
 */
export function ZonePicker({
  file,
  pages,
  initial,
  onCancel,
  onSave,
}: {
  file: File;
  pages: number;
  initial: { page: number; zones: Zone[] } | null;
  onCancel: () => void;
  onSave: (page: number, zones: Zone[]) => void;
}) {
  const [page, setPage] = useState(initial?.page ?? 1);
  const [zones, setZones] = useState<Zone[]>(initial?.zones ?? []);
  const [drag, setDrag] = useState<{ a: { x: number; y: number }; b: { x: number; y: number } } | null>(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const host = useRef<HTMLDivElement>(null);
  const overlay = useRef<HTMLDivElement>(null);

  // Blad tonen: PDF via pdf.js op een canvas, afbeelding als <img>.
  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};
    setReady(false);
    setError("");
    (async () => {
      try {
        if (isPdf(file)) {
          const task = getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
          cleanup = () => void task.destroy();
          const doc = await task.promise;
          const p = await doc.getPage(page);
          const base = p.getViewport({ scale: 1 });
          // Scherp genoeg om op in te zoomen, begrensd voor grote bladen (A0).
          const scale = Math.min(2400, 1200 * Math.min(2, window.devicePixelRatio || 1)) / base.width;
          const vp = p.getViewport({ scale });
          const c = document.createElement("canvas");
          c.width = Math.round(vp.width);
          c.height = Math.round(vp.height);
          c.className = "zonecanvas";
          c.setAttribute("role", "img");
          c.setAttribute("aria-label", `Blad ${page} van ${file.name}`);
          await p.render({ canvas: c, canvasContext: c.getContext("2d")!, viewport: vp }).promise;
          if (cancelled) return;
          host.current?.replaceChildren(c);
        } else {
          const url = URL.createObjectURL(file);
          const img = document.createElement("img");
          img.src = url;
          img.alt = file.name;
          img.className = "zonecanvas";
          await img.decode();
          cleanup = () => URL.revokeObjectURL(url);
          if (cancelled) return;
          host.current?.replaceChildren(img);
        }
        setReady(true);
      } catch {
        if (!cancelled) setError("De tekening kon niet worden getoond.");
      }
    })();
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [file, page]);

  const at = (e: ReactPointerEvent) => {
    const r = overlay.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!ready || e.button > 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = at(e);
    setDrag({ a: p, b: p });
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => drag && setDrag({ ...drag, b: at(e) });
  const onUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const r = normRect(drag.a, at(e));
    setDrag(null);
    if (r.w < MIN_ZONE || r.h < MIN_ZONE) return;
    setZones((zs) => [...zs, { ...r, name: `Zone ${zs.length + 1}`, level: "" }]);
  };
  const set = (i: number, patch: Partial<Zone>) =>
    setZones((zs) => zs.map((z, k) => (k === i ? { ...z, ...patch } : z)));
  const live = drag ? normRect(drag.a, drag.b) : null;
  const pct = (v: number) => `${v * 100}%`;
  const valid = zones.length > 0 && zones.every((z) => z.name.trim());

  return (
    <Modal
      title={`Zones kiezen · ${file.name}`}
      wide
      onClose={onCancel}
      footer={
        <>
          <span className="hint">
            {zones.length ? `${zones.length} ${zones.length === 1 ? "zone" : "zones"}` : "Nog geen zones"}
          </span>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onCancel}>
            Annuleren
          </button>
          <button type="button" className="btn primary" disabled={!valid} onClick={() => onSave(page, zones)}>
            Zones gebruiken
          </button>
        </>
      }
    >
      <p className="muted">
        Trek met muis of vinger een kader om elke verdieping op de tekening. Elk kader wordt een eigen verdieping met
        alleen dat deel van de tekening.
      </p>
      {pages > 1 && (
        <label className="field inline">
          <span className="label">Blad</span>
          <select
            value={page}
            onChange={(e) => {
              setPage(Number(e.target.value));
              setZones([]);
            }}
          >
            {Array.from({ length: pages }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                Blad {i + 1}
              </option>
            ))}
          </select>
        </label>
      )}
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <div className="zonestage">
        <div className="zoneinner">
          {/* Canvas/afbeelding wordt hier buiten React om geplaatst; de laadtekst staat er daarom naast. */}
          <div ref={host} className="zonehost" />
          {!ready && !error && <p className="muted zoneloading">Tekening laden…</p>}
          <div
            ref={overlay}
            className="zoneoverlay"
            data-testid="zone-overlay"
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={() => setDrag(null)}
          >
            {zones.map((z, i) => (
              <div
                key={i}
                className="zonebox"
                style={{
                  left: pct(z.x),
                  top: pct(z.y),
                  width: pct(z.w),
                  height: pct(z.h),
                  borderColor: COLORS[i % COLORS.length],
                }}
              >
                <span style={{ background: COLORS[i % COLORS.length] }}>
                  {i + 1} · {z.name}
                </span>
              </div>
            ))}
            {live && (
              <div
                className="zonebox live"
                style={{ left: pct(live.x), top: pct(live.y), width: pct(live.w), height: pct(live.h) }}
              />
            )}
          </div>
        </div>
      </div>
      {zones.length > 0 && (
        <ol className="zonelist">
          {zones.map((z, i) => (
            <li key={i}>
              <i aria-hidden="true" style={{ background: COLORS[i % COLORS.length] }}>
                {i + 1}
              </i>
              <input
                aria-label={`Naam verdieping zone ${i + 1}`}
                value={z.name}
                onChange={(e) => set(i, { name: e.target.value })}
              />
              <input
                aria-label={`Afkorting zone ${i + 1}`}
                className="short"
                value={z.level}
                placeholder="Afk."
                onChange={(e) => set(i, { level: e.target.value })}
              />
              <button
                type="button"
                className="btn ghost small"
                aria-label={`Zone ${i + 1} verwijderen`}
                onClick={() => setZones((zs) => zs.filter((_, k) => k !== i))}
              >
                <IconClose size={18} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
